/**
 * Motion Design System
 *
 * "Quiet confidence" - restrained, purposeful motion
 * Inspired by Linear, Vercel, Stripe editorial aesthetics
 */

// Easing curves for premium feel
export const EASE = {
  // Editorial mask reveal - "slide up" effect
  editorial: [0.2, 0.8, 0.2, 1] as const,
  // Expo out - snappy with soft landing
  outExpo: [0.16, 1, 0.3, 1] as const,
  // Circ out - smooth deceleration
  outCirc: [0, 0.55, 0.45, 1] as const,
  // Standard ease out
  out: [0.33, 1, 0.68, 1] as const,
  // Ease in-out for symmetrical motion
  inOut: [0.65, 0, 0.35, 1] as const,
}

// Duration scale (seconds)
export const DURATION = {
  feedback: 0.1,    // Button clicks, micro-interactions
  fast: 0.2,        // Hover states, small UI
  normal: 0.45,     // Standard UI transitions
  slow: 0.6,        // Section reveals
  entrance: 0.9,    // Hero elements
  hero: 1.2,        // Primary hero animation
}

// Stagger timings for children
export const STAGGER = {
  fast: 0.05,
  normal: 0.1,
  slow: 0.15,
}

// Spring physics for "heavy/expensive" feel
export const SPRING = {
  // Heavy - deliberate, premium
  heavy: { stiffness: 100, damping: 30 },
  // Soft bounce - playful but controlled
  soft: { stiffness: 200, damping: 25 },
  // Responsive - snappy UI
  responsive: { stiffness: 300, damping: 30 },
  // Bouncy - for accents
  bouncy: { stiffness: 400, damping: 15 },
}

// Common animation variants
export const variants = {
  // Fade in from bottom (editorial mask reveal effect)
  fadeInUp: {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: DURATION.slow,
        ease: EASE.editorial,
      }
    },
  },

  // Fade in with scale (for cards/images)
  fadeInScale: {
    hidden: {
      opacity: 0,
      scale: 0.98,
      filter: 'blur(4px)',
    },
    visible: {
      opacity: 1,
      scale: 1,
      filter: 'blur(0px)',
      transition: {
        duration: DURATION.slow,
        ease: EASE.outExpo,
      }
    },
  },

  // Simple fade
  fade: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: DURATION.normal, ease: EASE.out }
    },
  },

  // Stagger container
  staggerContainer: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: STAGGER.normal,
        delayChildren: 0.1,
      }
    },
  },

  // Fast stagger container
  staggerContainerFast: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: STAGGER.fast,
        delayChildren: 0.05,
      }
    },
  },

  // Card hover effect
  cardHover: {
    rest: {
      y: 0,
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    },
    hover: {
      y: -4,
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
      transition: {
        duration: DURATION.fast,
        ease: EASE.out,
      }
    },
  },

  // Button with icon nudge
  buttonHover: {
    rest: { scale: 1 },
    hover: {
      scale: 1.02,
      transition: {
        duration: DURATION.fast,
        ease: EASE.out,
      }
    },
    tap: { scale: 0.98 },
  },

  // Icon nudge (for arrows in buttons)
  iconNudge: {
    rest: { x: 0 },
    hover: {
      x: 3,
      transition: {
        duration: DURATION.fast,
        ease: EASE.out,
      }
    },
  },

  // Floating accent elements
  float: {
    initial: { y: 0 },
    animate: {
      y: [-6, 6, -6],
      transition: {
        duration: 4,
        ease: 'easeInOut',
        repeat: Infinity,
      }
    },
  },

  // Breathing glow effect
  breathe: {
    initial: { opacity: 0.4 },
    animate: {
      opacity: [0.4, 0.8, 0.4],
      transition: {
        duration: 3,
        ease: 'easeInOut',
        repeat: Infinity,
      }
    },
  },
}

// Hero animation sequence timings (in seconds)
export const HERO_SEQUENCE = {
  // Stage A: Background + Headline
  bgStart: 0,
  bgDuration: 0.6,
  headlineStart: 0.1,
  headlineDuration: 0.8,

  // Stage B: Browser mockup
  visualStart: 0.3,
  visualDuration: 0.9,

  // Stage C: Accent elements
  accentsStart: 0.8,
  accentsDuration: 0.6,

  // Total sequence
  total: 1.8,
}

// Scroll reveal defaults
export const SCROLL_REVEAL = {
  // Viewport margin - trigger before fully in view
  margin: '-100px',
  // Only animate once
  once: true,
  // Amount visible before triggering
  amount: 0.3,
}
